/**
 * `recurrence.open_cycle` (§5.2 advisory; `06-timeline.md` §10.4; B6 / #239) —
 * an ADVISORY notice that a `recurs_as` recurrence rule anchors against an
 * OPEN cycle (§4.1, ASOIAF seasons / WoT Ages).
 *
 * Open cycles have no fixed structural period — their instances' lengths are
 * determined retrospectively (`close_at`) — so a rule anchored against an open
 * cycle is held in **suspense**: `virtualInstances` synthesizes NO occurrences
 * for a still-open instance until it closes (`packages/calendar` §10.4). That
 * is correct behavior, not corruption — so this rule emits `info`, the
 * lowest-confidence editorial tier (`advisory.ts`). It NEVER refuses a write
 * (`blocking_error` is the only severity that does): the GM is simply notified
 * that "how many times does this recur during winter?" is undefined until the
 * season closes.
 *
 * Why a passed-in binding: predicate identity is an OPAQUE id (ADR-0004 §1)
 * with no semantic `recurs_as` flag, and "is this cycle open?" is calendar
 * semantics the caller resolves. So the set of `recurs_as` predicate ids AND
 * the calendars to resolve cycles against are supplied via
 * {@link AuditContext.recurrence_open_cycle} (mirroring how `causal.cycle`
 * takes its causal-predicate binding). Absent ⇒ the rule is a no-op
 * (progressive disclosure). The open-cycle detection itself reuses the single
 * `@dl/calendar` seam (`openCyclesReferencedBy`) the suspense path uses — one
 * definition of "references an open cycle", no fork.
 *
 * Owner-scoped (consistent with the other rules): only CANON `recurs_as` facts
 * are evaluated. A belief that an event recurs lives in a believer's layer and
 * never fires the canon notice.
 *
 * Deterministic + content-addressed + English-free: one advisory per
 * `(fact, open-cycle)` reference, stable id across runs, `message_key` +
 * `params` only (no baked prose).
 */
import { openCyclesReferencedBy } from '@dl/calendar';
import { RecurrenceRuleSchema, currentFactValue } from '@dl/shared';

import { factRef, type RawAdvisory } from '../advisory.js';
import type { AuditContext } from '../engine.js';

import { forEachFact } from './helpers.js';

/** The rule id — advisory `rule` + the §5.2.1 override key. */
export const RECURRENCE_OPEN_CYCLE_RULE = 'recurrence.open_cycle';

export function recurrenceOpenCycle(ctx: AuditContext): RawAdvisory[] {
  const binding = ctx.recurrence_open_cycle;
  if (!binding || binding.recurs_as_predicates.length === 0 || binding.calendars.length === 0) {
    return [];
  }
  const recursAs = new Set(binding.recurs_as_predicates);

  // One advisory per distinct (subject, factKey, open-cycle) reference, keyed
  // for de-duplication (a single fact can name the same open cycle twice — via
  // `rule.cycle` and a `labeled` anchor — but should yield one notice).
  const seen = new Set<string>();
  const out: RawAdvisory[] = [];

  forEachFact(ctx.entities, ({ entity, factKey, predicate, fact }) => {
    if (fact.owner.kind !== 'canon') return; // canon-only notice
    if (!recursAs.has(predicate)) return;

    const value = currentFactValue(fact);
    const parsed = RecurrenceRuleSchema.safeParse(value);
    if (!parsed.success) return; // malformed → value_kind_mismatch's job, not here

    // Resolve the open cycles this rule references across every declared
    // calendar (a cycle name is open if ANY active calendar declares it open).
    const openCycles = new Set<string>();
    for (const calendar of binding.calendars) {
      for (const cycle of openCyclesReferencedBy(calendar, parsed.data)) {
        openCycles.add(cycle);
      }
    }
    if (openCycles.size === 0) return;

    const via = factRef(entity.id, factKey);
    for (const cycle of [...openCycles].sort()) {
      const dedupeKey = `${via}::${cycle}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      // No one-click fix: the GM resolves this by closing the open cycle
      // instance (`close_at`) or re-anchoring the rule — neither is a single
      // fact mutation the §5.1 `remove_fact` intent can express.
      out.push({
        severity: 'info',
        rule: RECURRENCE_OPEN_CYCLE_RULE,
        message_key: 'auditor.recurrence.open_cycle',
        params: { cycle, entity: entity.id },
        fact_ids: [via],
        entity_ids: [entity.id],
      });
    }
  });

  // Deterministic ordering: by entity id, then cycle name. The engine re-sorts
  // globally, but a stable rule-local order keeps the unit assertions simple.
  return out.sort((a, b) => {
    const ea = a.entity_ids[0] ?? '';
    const eb = b.entity_ids[0] ?? '';
    if (ea !== eb) return ea < eb ? -1 : 1;
    const ca = String(a.params.cycle);
    const cb = String(b.params.cycle);
    return ca < cb ? -1 : ca > cb ? 1 : 0;
  });
}
